import { useCallback, useEffect, useState } from "react";

export const CATALOG_STATUS_TIMEOUT_MS = 20_000;
export const CATALOG_STATUS_RETRY_DELAY_MS = 500;

const INITIAL_STATE = { state: "verificando", conectado: null, error: "" };

function statusError(response, data) {
  if (!response.ok) {
    const message = data?.erro || data?.error || `A verificação do catálogo falhou (HTTP ${response.status}).`;
    const error = new Error(message);
    error.status = response.status;
    error.retryable = response.status === 429 || response.status >= 500;
    throw error;
  }
  if (!data || typeof data !== "object" || Array.isArray(data) || typeof data.conectado !== "boolean") {
    throw new Error("O servidor não confirmou o estado do catálogo.");
  }
  return data.conectado;
}

async function readStatus(response) {
  let data;
  try {
    data = await response.json();
  } catch {
    const error = new Error(`A resposta de verificação do catálogo é inválida (HTTP ${response.status}).`);
    error.status = response.status;
    throw error;
  }
  return statusError(response, data);
}

function waitForRetry() {
  let timer;
  const promise = new Promise(resolve => {
    timer = setTimeout(resolve, CATALOG_STATUS_RETRY_DELAY_MS);
  });
  return { promise, timer };
}

export default function useCatalogStatus() {
  const [retryKey, setRetryKey] = useState(0);
  const [status, setStatus] = useState(INITIAL_STATE);

  const retry = useCallback(() => setRetryKey(key => key + 1), []);

  useEffect(() => {
    let active = true;
    let retryTimer;
    let controller;
    setStatus(INITIAL_STATE);

    const check = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const attemptController = new AbortController();
        controller = attemptController;
        let timedOut = false;
        const timeout = setTimeout(() => {
          timedOut = true;
          attemptController.abort();
        }, CATALOG_STATUS_TIMEOUT_MS);
        try {
          const response = await fetch("/api/produtos/status", {
            credentials: "include",
            signal: attemptController.signal,
          });
          const conectado = await readStatus(response);
          if (active) setStatus({ state: conectado ? "conectado" : "desconectado", conectado, error: "" });
          return;
        } catch (error) {
          if (!active) return;
          const transient = timedOut || error?.retryable || error instanceof TypeError;
          if (attempt === 0 && transient) {
            setStatus({ state: "retentando", conectado: null, error: "" });
            const retry = waitForRetry();
            retryTimer = retry.timer;
            await retry.promise;
            if (!active) return;
          } else {
            const message = timedOut
              ? "A verificação do catálogo excedeu o limite de 20 segundos."
              : error?.message || "Não foi possível verificar a conexão do catálogo.";
            setStatus({ state: "falha", conectado: null, error: message });
            return;
          }
        } finally {
          clearTimeout(timeout);
        }
      }
    };

    check();
    return () => {
      active = false;
      clearTimeout(retryTimer);
      controller?.abort();
    };
  }, [retryKey]);

  return { ...status, retry };
}